// Elicitation decision page — the interview view for one pending
// request_human_input ask (DEV-16877 follow-up), reached from an
// "Agent questions" row in the Inbox. Sources its row from the same
// ['elicitations', 'pending'] query the list uses (already cached from
// navigation) rather than a dedicated single-item fetch — once resolved, the
// row simply drops out of that list.
import { useClient } from '@cortex/react-query';
import type { PersistentAgentElicitationInboxRow } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { BackButton } from '../../../components/common/BackButton';
import { EmptyState } from '../../../components/common/EmptyState';
import { PageHeader } from '../../../components/common/PageHeader';
import { Spinner } from '../../../components/common/Spinner';
import { ElicitationResolveForm } from '../../../components/elicitation/ElicitationResolveForm';
import { routes } from '../../../lib/constants';
import { formatRelative } from '../../../lib/format';

export const Route = createFileRoute('/inbox/elicitation/$elicitationId')({
  component: ElicitationDetailPage,
});

const sectionTitleStyle = {
  fontSize: 'var(--fs-xs)',
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: 'var(--text-secondary)',
  marginBottom: 'var(--sp-2)',
};

function ElicitationDetailPage() {
  const { elicitationId } = Route.useParams();
  const client = useClient();
  const nav = useNavigate();

  const pending = useQuery({
    queryKey: ['elicitations', 'pending'],
    queryFn: () =>
      client.http
        .get('v1/persistent-agent-elicitations/pending')
        .json<PersistentAgentElicitationInboxRow[]>(),
  });

  if (pending.isLoading) return <Spinner />;
  if (pending.error)
    return (
      <div className="alert alert--danger">Failed to load: {(pending.error as Error).message}</div>
    );

  const row = pending.data?.find((r) => r.id === elicitationId);
  if (!row)
    return (
      <EmptyState
        title="Not found"
        description="This question is no longer pending — it may already be answered, dismissed, or expired."
      />
    );

  return (
    <>
      <PageHeader
        title={row.persistentAgentName}
        actions={<BackButton to={routes.INBOX} label="Back to Inbox" />}
      />
      <div className="card">
        <div
          className="card-body"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}
        >
          <div>
            <p style={sectionTitleStyle}>Question</p>
            <p>{row.question}</p>
          </div>

          <div>
            <p style={sectionTitleStyle}>Answer</p>
            <ElicitationResolveForm
              row={row}
              onResolved={async () => {
                try {
                  await nav({ to: routes.INBOX });
                } catch (err) {
                  console.error(err);
                }
              }}
            />
          </div>

          <div>
            <p style={sectionTitleStyle}>Metadata</p>
            <p className="t-xs t-muted">
              {row.blocking
                ? 'Blocking — the thread is paused until this resolves.'
                : 'Non-blocking — the thread keeps working.'}
              {' · '}
              asked {formatRelative(row.createdAt)}
              {row.expiresAt ? <> · expires {formatRelative(row.expiresAt)}</> : null}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
